import React, { useState, useEffect } from 'react'
import DevLayout from '../../components/developer/DevLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'

export default function DevProfile() {
    const { user, token, updateUser } = useAuth()
    const [saving, setSaving] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [avatarUrl, setAvatarUrl] = useState('')
    const [avatarUploading, setAvatarUploading] = useState(false)
    const [form, setForm] = useState({
        name: '', phone: '', title: '', address: '', city: '', state: '', zip: '', about: '',
        facebook: '', twitter: '', googleplus: '', linkedin: '', projects: '',
        projectsSince: '', projectsCompleted: '0', projectsOngoing: '0', projectsUpcoming: '0'
    })

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || '', phone: user.phone || '', title: user.title || '',
                address: user.address || '', city: user.city || '', state: user.state || '',
                zip: user.zip || '', about: user.about || '',
                facebook: user.facebook || '', twitter: user.twitter || '',
                googleplus: user.googleplus || '', linkedin: user.linkedin || '',
                projects: user.projects || '',
                projectsSince: user.projectsSince || '',
                projectsCompleted: user.projectsCompleted || '0',
                projectsOngoing: user.projectsOngoing || '0',
                projectsUpcoming: user.projectsUpcoming || '0'
            })
            setAvatarUrl(user.avatar || '')
        }
    }, [user])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !token) return
        setAvatarUploading(true)
        const fd = new FormData()
        fd.append('image', e.target.files[0])
        try {
            const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd
            })
            const data = await res.json()
            if (data.url) {
                setAvatarUrl(data.url)
                setMsg({ type: 'success', text: 'Profile image uploaded! Click "Update My Profile" to save.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Image upload failed.' })
        } finally {
            setAvatarUploading(false)
        }
    }

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        setSaving(true); setMsg(null)
        try {
            const data = await api.updateMe(token, { ...form, avatar: avatarUrl })
            if (data && data._id) {
                updateUser(data)
                setMsg({ type: 'success', text: 'Profile updated successfully!' })
            } else {
                setMsg({ type: 'error', text: data?.message || 'Failed to update profile.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSaving(false)
        }
    }

    const btnColor = '#001b4a'
    const accentColor = '#f1ae24'
    const btnStyle = { backgroundColor: btnColor, color: accentColor, border: 'none' }

    return (
        <DevLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-black mb-1 text-black">My Profile</h3>
                    <p className="text-black fw-bold mb-0">Manage your personal information and social accounts.</p>
                </div>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} me-2`}></i>
                    {msg.text}
                </div>
            )}

            <form onSubmit={handleSave}>
                <div className="row g-4">
                    {/* Profile Image Card */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom"><h5 className="mb-0 fw-black text-black">Profile Image</h5></div>
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center gap-4 flex-wrap">
                                    <div className="position-relative">
                                        {avatarUrl ? (
                                            <img src={getImageUrl(avatarUrl)} alt="Profile"
                                                className="rounded-circle shadow-sm"
                                                style={{ width: 110, height: 110, objectFit: 'cover', border: `3px solid ${btnColor}` }} />
                                        ) : (
                                            <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                                                style={{ width: 110, height: 110, fontSize: '2.5rem', backgroundColor: btnColor }}>
                                                {user?.name?.charAt(0).toUpperCase() || 'D'}
                                            </div>
                                        )}
                                        {avatarUploading && (
                                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-circle"
                                                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                                <span className="spinner-border spinner-border-sm text-white"></span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <h6 className="fw-black text-black mb-1">{user?.name}</h6>
                                        <p className="text-black fw-bold small mb-2">{user?.email}</p>
                                        <label className="btn btn-sm px-4 py-2 rounded-pill fw-black" style={{ backgroundColor: btnColor, color: accentColor, cursor: 'pointer' }}>
                                            <i className="fa-solid fa-camera me-1"></i> {avatarUrl ? 'Change Photo' : 'Upload Photo'}
                                            <input type="file" accept="image/*" className="d-none" onChange={handleAvatarUpload} />
                                        </label>
                                        {avatarUrl && (
                                            <button type="button" className="btn btn-sm btn-outline-danger px-3 py-1 rounded-pill ms-2 fw-bold"
                                                onClick={() => { setAvatarUrl(''); setMsg({ type: 'success', text: 'Photo removed. Click "Update My Profile" to save.' }) }}>
                                                <i className="fa-solid fa-trash-can me-1"></i> Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Corporate Heritage & Stats */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-black text-black">Corporate Heritage / About</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="mb-4">
                                    <label className="form-label fw-bold text-black text-uppercase italic small">Provide a compelling description of the developer...</label>
                                    <div className="position-relative">
                                        <i className="fa-solid fa-quote-left text-slate-200 position-absolute top-0 start-0 m-3 fs-4"></i>
                                        <textarea className="form-control bg-white border-2 border-slate-200 p-4 ps-5 text-black fw-bold rounded-4" 
                                            name="about" value={form.about} onChange={handleChange} rows={5} placeholder="Start typing here..."></textarea>
                                    </div>
                                </div>
                                
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <div className="p-4 bg-white border-2 border-slate-100 rounded-4 text-center hover-shadow transition-all">
                                            <label className="form-label fw-black text-black text-uppercase small mb-3">Since (year)</label>
                                            <input type="text" className="form-control text-center bg-white border-2 border-slate-200 py-3 text-black fw-black rounded-3" 
                                                name="projectsSince" value={form.projectsSince} onChange={handleChange} placeholder="e.g. 1995" />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="p-4 bg-white border-2 border-slate-100 rounded-4 text-center hover-shadow transition-all">
                                            <label className="form-label fw-black text-black text-uppercase small mb-3">Completed</label>
                                            <input type="text" className="form-control text-center bg-white border-2 border-slate-200 py-3 text-emerald-600 fw-black rounded-3" 
                                                name="projectsCompleted" value={form.projectsCompleted} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="p-4 bg-white border-2 border-slate-100 rounded-4 text-center hover-shadow transition-all">
                                            <label className="form-label fw-black text-black text-uppercase small mb-3">Ongoing</label>
                                            <input type="text" className="form-control text-center bg-white border-2 border-slate-200 py-3 text-blue-600 fw-black rounded-3" 
                                                name="projectsOngoing" value={form.projectsOngoing} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="p-4 bg-white border-2 border-slate-100 rounded-4 text-center hover-shadow transition-all relative">
                                            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                                <label className="form-label fw-black text-black text-uppercase small mb-0">Upcoming</label>
                                                <div className="form-check form-switch m-0 p-0">
                                                    <input className="form-check-input ms-0" type="checkbox" role="switch" checked={parseInt(form.projectsUpcoming) > 0} readOnly />
                                                </div>
                                            </div>
                                            <input type="text" className="form-control text-center bg-white border-2 border-slate-200 py-3 text-orange-500 fw-black rounded-3" 
                                                name="projectsUpcoming" value={form.projectsUpcoming} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Basic Info */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom"><h5 className="mb-0 fw-black text-black">Basic Information</h5></div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase">Full Name</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="name" value={form.name} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase">Email (Read-only)</label>
                                        <input type="email" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" value={user?.email || ''} disabled readOnly />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase">Company / Title</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="title" value={form.title} onChange={handleChange} placeholder="e.g. XYZ Developers Pvt Ltd" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase">Phone Number</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="phone" value={form.phone} onChange={handleChange} placeholder="Your contact number" />
                                    </div>
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold text-black text-uppercase">Total Projects Display (e.g. 15+ Projects)</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="projects" value={form.projects} onChange={handleChange} placeholder="e.g. 15+ Projects" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom"><h5 className="mb-0 fw-black text-black">Office / Location</h5></div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold text-black text-uppercase">Address</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="address" value={form.address} onChange={handleChange} placeholder="Street address" />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-black text-uppercase">City</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="city" value={form.city} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-black text-uppercase">State</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="state" value={form.state} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-black text-uppercase">Zip Code</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="zip" value={form.zip} onChange={handleChange} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Social Media */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom"><h5 className="mb-0 fw-black text-black">Social Media Profiles</h5></div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase"><i className="fa-brands fa-facebook text-primary me-2"></i>Facebook</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="facebook" value={form.facebook} onChange={handleChange} placeholder="Link to Facebook" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase"><i className="fa-brands fa-twitter text-info me-2"></i>Twitter</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="twitter" value={form.twitter} onChange={handleChange} placeholder="Link to Twitter" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase"><i className="fa-brands fa-linkedin text-primary me-2"></i>LinkedIn</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="Link to LinkedIn" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-black text-uppercase"><i className="fa-brands fa-google text-danger me-2"></i>Google Plus</label>
                                        <input type="text" className="form-control bg-white border-2 border-slate-200 py-2 text-black fw-bold" name="googleplus" value={form.googleplus} onChange={handleChange} placeholder="Link to Google+" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-12 mb-5">
                        <div className="d-grid">
                            <button className="btn py-3 fw-black rounded-4 shadow-xl border-0" type="submit" disabled={saving} style={btnStyle}>
                                {saving ? (<><span className="spinner-border spinner-border-sm me-2"></span> Updating Profile...</>) : 'Update My Profile'}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </DevLayout>
    )
}
